import React from 'react';
import ReactDOM from 'react-dom';
import { css, cx } from '@emotion/css';
import { IconButton } from '@contrast-security-inc/design-system-buttons';
import { IconClose, theme } from '@contrast-security-inc/design-system-foundations';
const drawer = css `
  align-items: flex-start;
  box-shadow: 0px 0px 12px 6px rgba(0, 0, 0, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  padding: 0px;
  position: fixed;
  transition: transform 0.25s ease-in-out;
  white-space: nowrap;
  -ms-overflow-style: none;
  z-index: ${theme.zIndex.popover};

  ::-webkit-scrollbar {
    display: none;
  }
`;
const sizeChart = {
    sm: '25%',
    md: '50%',
    lg: '75%',
};
const placementChart = {
    right: {
        top: 0,
        right: 0,
        bottom: 'unset',
        left: 'unset',
    },
    left: {
        top: 0,
        right: 'unset',
        bottom: 'unset',
        left: 0,
    },
};
export const Drawer = ({ backgroundColor = theme.color.white, headerBackgroundColor = backgroundColor, body, className, title, header, isOpen, onClose, placement, size, testId, disableOffClick = false, }) => {
    const hasHeaderSection = title ?? header;
    const drawerRef = React.useRef(null);
    const drawerClass = React.useMemo(() => {
        return cx('ds-drawer', drawer, className);
    }, [className]);
    const getTransformValue = () => {
        if (isOpen) {
            return 'translateX(0%)';
        }
        return placement === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
    };
    React.useEffect(() => {
        if (!disableOffClick) {
            document.body.addEventListener('click', handleClickOutside, false);
            return () => {
                document.body.removeEventListener('click', handleClickOutside, false);
            };
        }
    });
    // Allow off click to close the drawer if disableOffClick is false
    const handleClickOutside = (event) => {
        if (isOpen && drawerRef.current && event.target.contains(drawerRef.current)) {
            onClose?.(false);
        }
    };
    // Allow Escape key to close the drawer
    React.useEffect(() => {
        const onKeyPress = (e) => {
            if (e.key === 'Escape') {
                onClose(isOpen);
            }
        };
        if (isOpen) {
            if (drawerRef.current) {
                drawerRef.current.focus();
            }
            window.addEventListener('keyup', onKeyPress);
        }
        return () => {
            window.removeEventListener('keyup', onKeyPress);
        };
    }, [isOpen, onClose]);
    return (React.createElement(React.Fragment, null, ReactDOM.createPortal(React.createElement("div", { className: css `
            width: ${isOpen ? '100%' : '0%'};
            position: absolute;
            top: 0;
            right: 0;
            height: 100%;
            display: flex;
          ` },
        React.createElement("div", { "aria-label": "Drawer open", ref: drawerRef, className: drawerClass, "data-testid": testId, role: "dialog", tabIndex: isOpen ? 0 : -1, style: {
                backgroundColor: backgroundColor,
                width: sizeChart[size],
                transform: getTransformValue(),
                top: placementChart[placement].top,
                right: placementChart[placement].right,
                bottom: placementChart[placement].bottom,
                left: placementChart[placement].left,
            } },
            React.createElement("div", { className: css `
                display: flex;
                flex-direction: column;
                height: 100vh;
                width: 100%;
              ` },
                React.createElement("div", { className: css `
                  display: flex;
                  justify-content: flex-end;
                  align-items: center;
                  width: 100%;
                  height: 48px;
                  padding: 24px 24px 0px;
                  box-sizing: border-box;
                  background-color: ${headerBackgroundColor};
                ` },
                    React.createElement(IconButton, { "aria-label": "Close Drawer", color: 'neutral80', onClick: () => onClose(isOpen), size: "md", style: { cursor: 'pointer' }, testId: "drawer-icon-close", Icon: IconClose })),
                hasHeaderSection && (React.createElement("div", { className: css `
                    display: flex;
                    flex-direction: column;
                    padding: 0 24px 16px;
                    width: 100%;
                    box-sizing: border-box;
                    border-bottom: 1px solid ${theme.color.neutral40};
                    background-color: ${headerBackgroundColor};
                  ` },
                    title && (React.createElement("div", { className: css `
                        text-wrap: wrap;
                        padding-bottom: 8px;
                      ` }, title)),
                    header && (React.createElement("div", { className: css `
                        text-wrap: wrap;
                      ` }, header)))),
                body && (React.createElement("div", { className: css `
                    height: 100%;
                    width: 100%;
                    padding: 24px;
                    box-sizing: border-box;
                    overflow: auto;
                  ` }, body))))), document.body)));
};
Drawer.displayName = 'Drawer';
